import { ITranslateProps } from 'models/common';
import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { BookingInputField, BookingSwitchField } from 'components/FormControls';
import {
	aboveZero,
	isNumber,
	isWholeNumber,
	minValue,
	priceFieldNormalization,
	required,
} from 'helpers/validationattributes';
import { Field } from 'redux-form';
import { PortalPageSection } from 'components/PortalStructure';
import { IShopCard } from 'models/shop/shopcard';
import { IOfficeMember } from 'models/shop/members';
import {
	IServiceEditForm as IForm,
	IServiceForm,
	IServiceFormServiceValues,
	IServiceFormValues,
	IServicesList,
} from 'models/shop/shopBookingTemplates';
import renderIcon from 'helpers/renderIcon';
import { CustomerCheckboxField } from '../CustomerCheckboxField';
import { getCurrencySymbol, readURLSearchParams } from 'helpers/common';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { calculateOnScreenServiceVariations } from '../../helpers';

const minValue1 = minValue(1);
const priceRequired = (val) => required(typeof val == 'number' && !isNaN(val) && isFinite(val) ? String(val) : val);

interface IProps extends ITranslateProps {
	officeId: string;
	shopCard: IShopCard;
	currentFormValues: IServiceForm;
	mode: 'add' | 'edit';
	serviceGroups: IServiceGroup[];
	servicesToEdit: IServicesList[];
	editModeServiceGroupId: string;
	isFetching?: boolean;
	timesResetServiceInBundle?: number;

	// form props
	change: any;
	initialize: (data: Partial<IServiceFormValues>) => void;
}

interface IState {
	priceFieldCursor: number;
	hasSubservice: boolean;
	totalServicesCreatedLength: number;
	onScreenServicesLength: number;
	hasSetEditServices: boolean;

	onScreenVariations: number;
	priceFieldsRefs: React.RefObject<any>[];
}

type IPerformer = IOfficeMember & IForm['performers'][string][string];

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ServiceSettings extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			priceFieldCursor: null,
			hasSubservice: false,
			totalServicesCreatedLength: 1,
			onScreenServicesLength: 1,
			onScreenVariations: 1,
			hasSetEditServices: false,
			priceFieldsRefs: [],
		};

		this.addNewSubservice = this.addNewSubservice.bind(this);
		this.renderNormalServicePerformers = this.renderNormalServicePerformers.bind(this);
		this.renderNormalService = this.renderNormalService.bind(this);
		this.renderVariations = this.renderVariations.bind(this);
		this.renderServices = this.renderServices.bind(this);
		this.setAllStaff = this.setAllStaff.bind(this);
		this.updatePriceFieldRefs = this.updatePriceFieldRefs.bind(this);
	}

	public componentDidMount() {
		// Set the intiial service settings when in add mode
		if (this.props.mode === 'add' && this.props.currentFormValues === null) {
			this.props.change(`services.service1.serviceSettings.bookability`, true);
			this.props.change(`services.service1.serviceSettings.customPriceAndDuration`, false);

			// Set the service category and group if the user came from service group dropdown Add component
			const serviceCategory = Number(readURLSearchParams(c.SERVICE_CATEGORY));
			const serviceGroup = readURLSearchParams(c.SERVICE_GROUP);

			if (serviceCategory && serviceGroup) {
				this.props.change('info.serviceCategory', serviceCategory);
				this.props.change('info.serviceGroup', serviceGroup);
			}
		}

		this.updatePriceFieldRefs();
	}

	public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): void {
		// Set initial edit values
		if (
			this.props.mode === 'edit' &&
			!this.state.hasSetEditServices &&
			prevProps.isFetching &&
			!this.props.isFetching
		) {
			const serviceGroupData = this.props.serviceGroups.find((sg) => sg.id === this.props.editModeServiceGroupId);
			const initialValuesObject: Partial<IServiceFormValues> = {
				info: {
					serviceName: serviceGroupData.value,
					serviceCategory: serviceGroupData.serviceCategoryId,
					serviceGroup: serviceGroupData.parentId,
					serviceDescription: this.props.servicesToEdit[0].description,
				},
				globalServiceSettings: {
					onlinePayment: true, // change once implemented
					onsitePayment: true, // change once implemented
				},
			};

			// Group variations by variantParentId
			const groupServices = (items, key) =>
				items.reduce(
					(prevVal, curVal) => ({
						...prevVal,
						[curVal[key]]: [...(prevVal[curVal[key]] || []), curVal],
					}),
					{},
				);

			// Sort services by order
			const sortedServices = [...this.props.servicesToEdit].sort((a, b) => a.order - b.order);

			// Group services by variantParentId (to get whole variation services)
			const groupedServices = groupServices(sortedServices, 'variantParentId');

			const initialServices: IServiceFormServiceValues = {};
			// const initialCustomTimelineTracker = {};

			// Go through each service
			Object.keys(groupedServices).forEach((service, idx) => {
				// Set normal service
				if (groupedServices[service].length === 1) {
					groupedServices[service].map((variationService: IServicesList) => {
						const performersObject: any = {};

						variationService.resources.map((performer: any) => {
							performersObject[performer.id] = true;
						});

						initialServices[`service${idx + 1}`] = {
							serviceSettings: {
								bookability: variationService.published,
								customPriceAndDuration: false, // Not a variation
							},
							serviceVariations: {
								normalService: {
									duration: Number(variationService.duration) / 60, // must be in minutes
									price: variationService.originalPrice,
									performers: performersObject,
									bookingTemplateId: variationService.id,
								},
							},
							info: {
								subServiceName: variationService?.name,
								variantParentId: variationService?.variantParentId,
							},
						};
					});
				}
				// Set variation service
				if (groupedServices[service].length > 1) {
					const initialVariationServiceSettings = {
						bookability: groupedServices[service][0].published,
						customPriceAndDuration: true, // Variation
					};
					const initialSubserviceInfo = {
						subServiceName: groupedServices[service][0].name,
						variantParentId: groupedServices[service][0].variantParentId,
					};
					let initialVariationServicesData = {};
					const variationsObject = {};

					// Go through each variation add idxVar here when timelines are implemented
					groupedServices[service].map((variationService) => {
						// Go through existing variation services's resources and convert them to form values
						variationService.resources.map((performer: any) => {
							variationsObject[performer.id] = {
								duration: Number(variationService.duration) / 60, // must be in minutes
								price: variationService.originalPrice,
								performers: { [performer.id]: true },
								bookingTemplateId: variationService.id,
							};
						});
					});

					if (this.currentOfficePerformers?.length && Object.keys(variationsObject).length !== 0) {
						const variationsObjectValues = {};

						// Assign variation service based on the performer id
						this.currentOfficePerformers.map((per, idx) => {
							const variationsObjectKey = Object.keys(variationsObject).find((key) => key === per.id);
							if (variationsObjectKey) {
								variationsObjectValues[`variation${idx + 1}`] = variationsObject[variationsObjectKey];
							}
						});
						if (Object.keys(variationsObjectValues).length !== 0) {
							initialVariationServicesData = { ...variationsObjectValues };
						}
					}

					initialServices[`service${idx + 1}`] = {
						serviceSettings: initialVariationServiceSettings,
						serviceVariations: initialVariationServicesData,
						info: initialSubserviceInfo,
					};
				}
			});

			initialValuesObject.services = initialServices;

			this.setState({
				hasSetEditServices: true,
				hasSubservice: Object.keys(initialServices).length > 1,
				totalServicesCreatedLength: Object.keys(initialServices).length,
				onScreenServicesLength: Object.keys(initialServices).length,
				onScreenVariations: calculateOnScreenServiceVariations(initialServices),
			});
			this.props.initialize(initialValuesObject);
		}

		// If the saving / deleting failed due to a service being in a bundle
		if (prevProps?.timesResetServiceInBundle !== this.props.timesResetServiceInBundle) {
			const newServicesLength = this.props?.currentFormValues?.values?.services
				? Object.keys(this.props?.currentFormValues?.values?.services)?.length
				: null;
			if (newServicesLength) {
				this.setState({
					hasSubservice: newServicesLength > 1 ? true : false,
					onScreenServicesLength: newServicesLength,
				});
			}
		}

		const prevServices = prevProps?.currentFormValues?.values?.services;
		const currentServices = this.props?.currentFormValues?.values?.services;

		// Check for on screen price field changes and update field references if needed
		// Compare only needed values that could change how many price fields are currently registered in form
		if (
			prevServices &&
			currentServices &&
			(Object.keys(prevServices).length !== Object.keys(currentServices).length ||
				Object.keys(currentServices).some((serKey) => {
					const prevService = prevServices[serKey];
					const currentService = currentServices[serKey];

					// Check if service added, deleted or properties that affect the price field visibility changed
					return (
						!prevService ||
						!currentService ||
						prevService.deleted !== currentService.deleted ||
						prevService.serviceSettings?.customPriceAndDuration !==
							currentService.serviceSettings?.customPriceAndDuration ||
						(currentService.serviceVariations &&
							Object.keys(currentService.serviceVariations).some((varKey) => {
								const prevVariation = prevService.serviceVariations?.[varKey];
								const currentVariation = currentService.serviceVariations?.[varKey];

								// Check if variation added, deleted or performers of variation changed (check only for variations, unnecessary to check for normal service)
								return (
									!prevVariation ||
									!currentVariation ||
									(prevService.serviceSettings?.customPriceAndDuration === true &&
										currentService.serviceSettings?.customPriceAndDuration === true &&
										JSON.stringify(prevVariation.performers) !== JSON.stringify(currentVariation.performers))
								);
							}))
					);
				}))
		) {
			// Calculate number of normal services and variations currently on screen
			this.setState({
				onScreenVariations: calculateOnScreenServiceVariations(this.props.currentFormValues.values.services),
			});
		}

		// Update price fields references
		if (prevState?.onScreenVariations !== this.state?.onScreenVariations) {
			this.updatePriceFieldRefs();
		}
	}

	public componentWillUnmount() {}

	private addNewSubservice = () => {
		const newTotalLength = this.state.totalServicesCreatedLength + 1;
		const newOnScreenLength = this.state.onScreenServicesLength + 1;

		this.setInitialNewServiceSettings(newTotalLength);
		this.setState({
			totalServicesCreatedLength: newTotalLength,
			onScreenServicesLength: newOnScreenLength,
			hasSubservice: newOnScreenLength > 1,
		});
	};

	private removeSingleService = (fieldName) => {
		const newOnScreenLength = this.state.onScreenServicesLength - 1;

		this.props.change(`${fieldName}.deleted`, true);
		this.setState({ onScreenServicesLength: newOnScreenLength, hasSubservice: newOnScreenLength > 1 });
	};

	private setInitialNewServiceSettings = (serviceIndex) => {
		this.props.change(`services.service${serviceIndex}.serviceSettings.bookability`, true);
		this.props.change(`services.service${serviceIndex}.serviceSettings.customPriceAndDuration`, false);
	};

	private setAllStaff = (serviceIndex: number, isSameLength: boolean) => {
		const performersObject = {};

		if (isSameLength === true) {
			this.currentOfficePerformers.map((performer) => {
				performersObject[performer.id] = false;
			});

			this.props.change(`services.service${serviceIndex}.serviceVariations.normalService.performers`, performersObject);
		} else {
			this.currentOfficePerformers.map((performer) => {
				performersObject[performer.id] = true;
			});

			this.props.change(`services.service${serviceIndex}.serviceVariations.normalService.performers`, performersObject);
		}
	};

	private get currentOfficePerformers(): IPerformer[] {
		const { officeId, shopCard } = this.props;
		const currentOffice = shopCard?.offices.find((o) => o.id === officeId) || { staff: [] };
		const staffMembers: Array<IOfficeMember> = currentOffice?.staff || [];

		return staffMembers.map((per) => ({
			...per,
		}));
	}

	private updatePriceFieldRefs = () => {
		const { onScreenVariations } = this.state;
		const fieldRefs = Array.from({ length: onScreenVariations }, () => React.createRef());
		this.setState({ priceFieldsRefs: fieldRefs });
	};

	private renderNormalServicePerformers = (serviceIndex: number) => {
		const { t } = this.props;
		const formValues = this.props.currentFormValues?.values;

		if (!this.props.shopCard) {
			return;
		}

		let countSelected = 0;
		let isSameLength = false;

		if (formValues?.services[`service${serviceIndex}`]?.serviceVariations?.normalService?.performers) {
			const membersBooleanArray: boolean[] = Object.values(
				formValues?.services[`service${serviceIndex}`]?.serviceVariations?.normalService?.performers,
			);

			membersBooleanArray.map((mba) => {
				if (mba === true) {
					countSelected = countSelected + 1;
				}
			});
		}

		if (countSelected === this.currentOfficePerformers.length) {
			isSameLength = true;
		} else {
			isSameLength = false;
		}

		let selectAllElement: JSX.Element = null;

		if (countSelected === 0) {
			selectAllElement = renderIcon(
				'unchecked',
				24,
				24,
				'singleAddEditService__serviceSettings__staffContainer__selectAll__icon',
			);
		} else if (isSameLength === true) {
			selectAllElement = renderIcon(
				'checked',
				24,
				24,
				'singleAddEditService__serviceSettings__staffContainer__selectAll__icon',
			);
		} else {
			selectAllElement = renderIcon(
				'partially_checked',
				24,
				24,
				'singleAddEditService__serviceSettings__staffContainer__selectAll__icon',
			);
		}

		return (
			<div className="row">
				<div className="col-12">
					<div
						className="singleAddEditService__serviceSettings__staffContainer__selectAll"
						onClick={() => this.setAllStaff(serviceIndex, isSameLength)}
					>
						{selectAllElement}
						<span className="singleAddEditService__serviceSettings__staffContainer__selectAll__text">
							{t('BSPortal_Common_SelectAll')}
						</span>
						<span className="singleAddEditService__serviceSettings__staffContainer__selectAll__grayText">
							({this.currentOfficePerformers.length})
						</span>
					</div>
				</div>
				{this.currentOfficePerformers.map((per, idx) => {
					return (
						<div className="col-md-6 col-12" key={per.id + idx}>
							<Field
								name={`services.service${serviceIndex}.serviceVariations.normalService.performers.${per.id}`}
								component={CustomerCheckboxField}
								type="checkbox"
								label={per.name}
								image={per.picture}
								jobTitle={per.jobTitle}
							/>
						</div>
					);
				})}
			</div>
		);
	};

	private renderNormalService = (i: number, refObject: { refIndex: number }) => {
		const { t } = this.props;
		refObject.refIndex = refObject.refIndex + 1;

		return (
			<div className="row">
				<div className="col-md-6 col-12">
					<Field
						name={`services.service${i + 1}.serviceVariations.normalService.price`}
						component={BookingInputField}
						type="text"
						label={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(this.props?.shopCard?.currencyKey)})`}
						validate={[priceRequired, isNumber]}
						customRef={this.state.priceFieldsRefs[refObject.refIndex - 1]}
						customCursor={this.state.priceFieldCursor}
						onChangeAction={(value) => {
							this.setState({ priceFieldCursor: value.target.selectionStart });
							const decimalValue = priceFieldNormalization(value.target.value);
							return (value.target.value = decimalValue);
						}}
						min={0}
						step={0.01}
						inputMode={'decimal'}
						normalize={aboveZero}
						information={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(
							this.props?.shopCard?.currencyKey,
						)})`}
					/>
				</div>

				<div className="col-md-6 col-12">
					<Field
						name={`services.service${i + 1}.serviceVariations.normalService.duration`}
						component={BookingInputField}
						type="text"
						label={`${t('User_ShopCard_Services_ServiceDuration')} (min)`}
						validate={[required, minValue1, isWholeNumber]}
						min={1}
						step={0.01}
						inputMode={'decimal'}
						normalize={aboveZero}
						information={`${t('User_ShopCard_Services_ServiceDuration')} (min)`}
					/>
				</div>

				<div className="col-12">
					<h4 className="singleAddEditService__serviceSettings__serviceContainer__sectionTitle">
						{t('User_ShopCard_Services_Staff')}
					</h4>

					<p className="singleAddEditService__serviceSettings__serviceContainer__sectionSubtext">
						{t('User_ShopCard_Services_AssignMembers')}
					</p>

					{this.renderNormalServicePerformers(i + 1)}
				</div>
			</div>
		);
	};

	private renderVariations = (i: number, refObject: { refIndex: number }) => {
		const { t } = this.props;

		if (!this.props.shopCard) {
			return;
		}

		return (
			<div className="singleAddEditService__serviceSettings__variationsContainer">
				<h4 className="singleAddEditService__serviceSettings__serviceContainer__sectionTitle">
					{t('User_ShopCard_Services_Staff')}
				</h4>
				<p className="singleAddEditService__serviceSettings__serviceContainer__sectionSubtext">
					{t('User_ShopCard_Services_AssignMembers')}
				</p>

				{this.currentOfficePerformers.map((per, idx) => {
					let showOptions = false;

					// Check if the current member is selected and display price and duration fields for them if selected
					if (
						this.props.currentFormValues?.values?.services?.[`service${i + 1}`]?.serviceVariations?.[
							`variation${idx + 1}`
						]?.performers?.[per.id] === true
					) {
						showOptions = true;

						refObject.refIndex = refObject.refIndex + 1;
					}

					return (
						<div className="singleAddEditService__serviceSettings__variation" key={per.id}>
							<div className="row">
								<div className="col-12">
									<Field
										name={`services.service${i + 1}.serviceVariations.variation${idx + 1}.performers.${per.id}`}
										component={CustomerCheckboxField}
										type="checkbox"
										label={per.name}
										key={idx}
										image={per.picture}
										jobTitle={per.jobTitle}
									/>
								</div>

								{showOptions && (
									<>
										<div className="col-md-6 col-12">
											<Field
												name={`services.service${i + 1}.serviceVariations.variation${idx + 1}.price`}
												component={BookingInputField}
												type="text"
												label={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(
													this.props?.shopCard?.currencyKey,
												)})`}
												validate={[priceRequired, isNumber]}
												customRef={this.state.priceFieldsRefs[refObject.refIndex - 1]}
												customCursor={this.state.priceFieldCursor}
												onChangeAction={(value) => {
													this.setState({ priceFieldCursor: value.target.selectionStart });
													const decimalValue = priceFieldNormalization(value.target.value);
													return (value.target.value = decimalValue);
												}}
												min={0}
												step={0.01}
												inputMode={'decimal'}
												normalize={aboveZero}
												information={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(
													this.props?.shopCard?.currencyKey,
												)})`}
											/>
										</div>
										<div className="col-md-6 col-12">
											<Field
												name={`services.service${i + 1}.serviceVariations.variation${idx + 1}.duration`}
												component={BookingInputField}
												type="text"
												label={`${t('User_ShopCard_Services_ServiceDuration')} (min)`}
												validate={[required, minValue1, isWholeNumber]}
												min={1}
												step={0.01}
												inputMode={'decimal'}
												normalize={aboveZero}
												information={`${t('User_ShopCard_Services_ServiceDuration')} (min)`}
											/>
										</div>
									</>
								)}

								{idx + 1 !== this.currentOfficePerformers.length && (
									<div className="col-12 singleAddEditService__serviceSettings__variationLineDesktop">
										<div className="singleAddEditService__serviceSettings__variationLine"></div>
									</div>
								)}

								{idx + 1 !== this.currentOfficePerformers.length && showOptions && (
									<div className="col-12 singleAddEditService__serviceSettings__variationLineMobile">
										<div className="singleAddEditService__serviceSettings__variationLine"></div>
									</div>
								)}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	private renderServiceSettings(serviceIndex) {
		const { t } = this.props;

		return (
			<div className="singleAddEditService__serviceSettings__settingsContainer">
				<div className="singleAddEditService__serviceSettings__settingsContainer__bookability">
					<Field
						name={`services.service${serviceIndex}.serviceSettings.bookability`}
						type="checkbox"
						component={BookingSwitchField}
						label={t('User_ShopCard_Services_CanOnlyBeBookedManually')}
					/>
				</div>
			</div>
		);
	}

	private renderServices = () => {
		const { t } = this.props;

		const serviceElements = [];
		if (this.props.currentFormValues?.values?.services) {
			let titleNumber = 1;

			// Keep track of the reference index (increments inside renderVariations / renderNormalService functions)
			const refObject = { refIndex: 0 };

			// Go through each service and filter out deleted ones
			Object.keys(this.props.currentFormValues?.values?.services).forEach((_ser, idx) => {
				const service = this.props.currentFormValues.values.services[`service${idx + 1}`]; //|| [];
				if (!service?.deleted) {
					serviceElements.push(
						<PortalPageSection className="singleAddEditService__serviceSettings__serviceContainer" key={idx + 1}>
							<h3 className="singleAddEditService__serviceSettings__serviceContainer__title">
								{t('User_ShopCard_Services_ServiceVariation').toUpperCase()} {titleNumber}
							</h3>
							<h4 className="singleAddEditService__serviceSettings__serviceContainer__sectionTitle">
								{t('User_ShopCard_Services_PricingDuration')}
							</h4>
							<p className="singleAddEditService__serviceSettings__serviceContainer__sectionSubtext">
								{t('User_ShopCard_Services_SetPricingDuration')}
							</p>
							{this.state.hasSubservice ? (
								<Field
									className="singleAddEditService__basicInformation__serviceName"
									name={`services.service${idx + 1}.info.subServiceName`}
									component={BookingInputField}
									type="text"
									label={t('User_ShopCard_Services_VariationName')}
									information={t('User_ShopCard_Services_VariationName')}
									validate={[required]}
									placeholder={t('User_ShopCard_Example_Long_Hair')}
								/>
							) : null}
							<div className="singleAddEditService__serviceSettings__serviceContainer__customSettings">
								<Field
									name={`services.service${idx + 1}.serviceSettings.customPriceAndDuration`}
									type="checkbox"
									component={BookingSwitchField}
									label={t('User_ShopCard_Services_CustomPriceAndDurationPerStaff')}
								/>
							</div>
							{this.props.currentFormValues?.values?.services &&
							this.props.currentFormValues?.values?.services[`service${idx + 1}`]?.serviceSettings
								?.customPriceAndDuration
								? this.renderVariations(idx, refObject)
								: this.renderNormalService(idx, refObject)}
							<h4 className="singleAddEditService__serviceSettings__serviceContainer__sectionTitle">
								{t('User_ShopCard_Services_BookingSettings')}
							</h4>
							<p className="singleAddEditService__serviceSettings__serviceContainer__sectionSubtext">
								{t('User_ShopCard_Services_SetBookingVisibility')}
							</p>
							{this.renderServiceSettings(idx + 1)}
							{/** Don't show delete for first service */}
							{idx > 0 && (
								<div className="singleAddEditService__serviceSettings__serviceContainer__deleteServiceContainer">
									<div
										className="singleAddEditService__serviceSettings__serviceContainer__deleteService"
										onClick={() => this.removeSingleService(`services.service${idx + 1}`)}
									>
										{renderIcon(
											'delete',
											20,
											20,
											'singleAddEditService__serviceSettings__serviceContainer__deleteService__icon',
										)}
										<span className="singleAddEditService__serviceSettings__serviceContainer__deleteService__text">
											{t('User_ShopCard_Services_DeleteService')}
										</span>
									</div>
								</div>
							)}
						</PortalPageSection>,
					);
					titleNumber = titleNumber + 1;
				}
			});
		}

		return serviceElements;
	};

	render() {
		const { t } = this.props;

		return (
			<div className="singleAddEditService__serviceSettings">
				<h2 className="singleAddEditService__serviceSettings__title">{t('User_ShopCard_Services_ServiceSettings')}</h2>
				<p className="singleAddEditService__serviceSettings__subText">
					{t('User_ShopCard_Services_ServiceSettingsDefinition')}
				</p>
				{this.renderServices()}
				<div className="singleAddEditService__serviceSettings__addSubservice" onClick={() => this.addNewSubservice()}>
					{renderIcon('plus_thin', 14, 14, 'singleAddEditService__serviceSettings__addSubservice__plus')}
					<span>{t('User_ShopCard_Services_AddSubservice')}</span>
				</div>
			</div>
		);
	}
}

export { ServiceSettings };
